import {
  ApolloClient,
  InMemoryCache,
  NormalizedCacheObject,
  createHttpLink,
  from,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { Observable, fromPromise } from 'apollo-link';
import { createClient } from 'graphql-ws';
import { jwtDecode } from 'jwt-decode';
import pRetry from 'p-retry';

import { Logger } from '@eluve/logger';

import {
  ApolloClientFactoryOptions,
  defaultApolloClientOptions,
} from './client-options';

const webClientLogger = new Logger('apollo-client.factory');

const JWT_ERRORS = ['JWTExpired', 'Malformed Authorization header'];

// Refresh proactively when less than this much life remains so we don't
// race Hasura's clock at the request boundary.
const PROACTIVE_REFRESH_MARGIN_MS = 30_000;

const isJwtExpiringSoon = (jwt: string): boolean => {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(jwt);
    if (!exp) {
      return true;
    }
    return exp * 1000 - Date.now() < PROACTIVE_REFRESH_MARGIN_MS;
  } catch {
    // Malformed JWT — treat the same as a missing exp claim and refresh.
    return true;
  }
};

export const apolloClientFactory: (
  options: ApolloClientFactoryOptions & {
    logger?: Logger;
  },
) => ApolloClient<NormalizedCacheObject> = (options) => {
  const {
    uri,
    cacheInstance,
    defaultOptions = defaultApolloClientOptions,
    enableSubscriptions = true,
    desiredRole,
    accessTokenProvider,
    logger = webClientLogger,
  } = options;

  const retrieveToken = async (options?: { hardRefresh?: boolean }) => {
    const { jwt, role: sessionRole } = await pRetry(
      async () => accessTokenProvider(options),
      {
        retries: 3,
        minTimeout: 1000,
        maxTimeout: 1000,
      },
    );

    const resolvedDesiredRole =
      typeof desiredRole === 'function' ? await desiredRole() : desiredRole;
    const role = resolvedDesiredRole ?? sessionRole;

    return {
      jwt,
      role,
    };
  };

  // Soft fetch first, then escalate to a hard refresh if the JWT is within
  // the proactive margin. Used by both authLink and the WS connectionParams.
  const getProactivelyRefreshedToken = async () => {
    let { jwt, role } = await retrieveToken();
    if (isJwtExpiringSoon(jwt)) {
      ({ jwt, role } = await retrieveToken({ hardRefresh: true }));
    }
    return { jwt, role };
  };

  const authLink = setContext(async (_request, { headers }) => {
    const { jwt, role } = await getProactivelyRefreshedToken();

    return {
      headers: {
        ...headers,
        Authorization: `Bearer ${jwt}`,
        'x-hasura-role': headers?.['x-hasura-role'] ?? role,
      },
    };
  });

  /**
   * If the request fails due to a JWT error from Hasura (expired, malformed,
   * or invalid signature — the latter happens when SuperTokens rotates its
   * dynamic signing key and the cached access token was signed by the
   * previous key), refresh the token and retry the operation once.
   *
   * Hasura returns these errors as HTTP 200 with `extensions.code: invalid-jwt`,
   * so the SuperTokens website SDK's 401-based interceptor never fires —
   * Apollo has to drive the refresh.
   */
  const retryErrorLink = onError(
    ({ graphQLErrors, networkError, operation, forward }) => {
      const { operationName } = operation;

      const context = operation.getContext() as {
        cache?: InMemoryCache;
        __jwtRefreshAttempted?: boolean;
      };

      if (networkError) {
        // If this is a network error but we've got cached data show that instead of an error
        // state in the UI
        const cachedQueryData = context.cache?.readQuery({
          query: operation.query,
          variables: operation.variables,
        });

        if (cachedQueryData) {
          return Observable.of({ data: cachedQueryData }) as any;
        }
      }

      const isJwtError = (e: { message: string; extensions?: unknown }) => {
        const code = (e.extensions as { code?: string } | undefined)?.code;
        return (
          code === 'invalid-jwt' ||
          JWT_ERRORS.some((errorText) => e.message.includes(errorText))
        );
      };

      if (
        graphQLErrors &&
        !context.__jwtRefreshAttempted &&
        graphQLErrors.some(isJwtError)
      ) {
        logger.warn(
          `Found invalid JWT. Attempting to refresh token and retry operation ${operationName}`,
        );

        return fromPromise(
          retrieveToken({
            hardRefresh: true,
          }),
        ).flatMap(({ jwt }) => {
          operation.setContext(({ headers = {} }) => ({
            __jwtRefreshAttempted: true,
            headers: {
              ...headers,
              Authorization: `Bearer ${jwt}`,
            },
          }));

          return forward(operation);
        });
      }

      if (graphQLErrors) {
        const hasInvalidJwtSignature = graphQLErrors.some(
          (e) =>
            e.extensions?.['code'] === 'invalid-jwt' &&
            e.message.includes('JWSInvalidSignature'),
        );

        const opContext = operation.getContext() as {
          headers?: Record<string, string>;
          __jwsRetryAttempted?: boolean;
        };

        if (hasInvalidJwtSignature && !opContext.__jwsRetryAttempted) {
          let jwtKid: string | undefined;
          try {
            const token = (opContext.headers?.['Authorization'] ?? '').replace(
              'Bearer ',
              '',
            );
            if (token) {
              const header = jwtDecode(token, { header: true }) as {
                kid?: string;
              };
              jwtKid = header.kid;
            }
          } catch {
            // ignore decode errors
          }

          logger.warn(
            'JWS signature failure; hard-refreshing token and retrying once',
            { operationName, jwtKid },
          );

          return fromPromise(retrieveToken({ hardRefresh: true })).flatMap(
            ({ jwt }) => {
              operation.setContext(({ headers = {} }) => ({
                headers: {
                  ...headers,
                  Authorization: `Bearer ${jwt}`,
                },
                __jwsRetryAttempted: true,
              }));

              return forward(operation);
            },
          );
        }

        logger.error('Unhandled error in session client factory', {
          operationName,
          graphQLErrors,
        });
      }

      if (networkError) {
        logger.warn('Network error in session client factory', {
          networkError,
        });
      }

      return;
    },
  );

  const retryNetworkLink = new RetryLink({
    attempts: {
      max: 3,
    },
  });

  const httpLink = from([
    authLink,
    retryErrorLink,
    retryNetworkLink,
    createHttpLink({
      uri,
      fetch,
    }),
  ]);

  let finalLink = httpLink;

  // If subscriptions are enabled we need to set up a websocket and then split control
  // over the operations so that subscriptions will be handled by the WS Link
  if (enableSubscriptions) {
    const [protocol, endpoint] = uri.split('//');
    if (!protocol || !endpoint) {
      throw new Error(`Invalid URI provided: ${uri}`);
    }
    const wsUri = protocol.startsWith('https')
      ? `wss://${endpoint}`
      : `ws://${endpoint}`;

    // Set when a WS close/error indicates Hasura rejected our JWT on
    // signature; consumed on the next connectionParams call to force a
    // hard refresh before reconnecting (mirrors the HTTP retry path).
    let forceHardRefreshOnNextConnect = false;

    const JWT_SIGNATURE_FAILURE_PATTERN = /JWSInvalidSignature|invalid-jwt/i;

    const wsLink = new GraphQLWsLink(
      createClient({
        url: wsUri,
        // Per graphql-ws maintainer guidance (discussion #275): use Infinity
        // with the lib's default exponential backoff so a flaky network or
        // laptop wake doesn't leave subscriptions silently dead. The lib's
        // own fatal-close-code list (4400/4401/4406/4409/4429/4500) still
        // short-circuits retries on truly-fatal errors regardless.
        retryAttempts: Infinity,
        // The lib's default shouldRetry is `isLikeCloseEvent`, which rejects
        // non-CloseEvent errors (e.g. connectionParams throwing during a JWT
        // refresh) before isFatalConnectionProblem ever runs — see
        // client.js:312-348 in graphql-ws@5.14.2. Override so transient
        // connection-phase errors stay in the retry loop. The fatal-close-code
        // list (4400/4401/4406/4409/4429/4500) is checked first and still
        // short-circuits before reaching this callback.
        shouldRetry: () => true,
        // Ungraceful disconnects (server kill, proxy reset, browser network
        // change after sleep) emit no close code and would otherwise be
        // treated as fatal — keep retrying instead.
        isFatalConnectionProblem: () => false,
        connectionParams: async () => {
          const { jwt, role } = forceHardRefreshOnNextConnect
            ? await retrieveToken({ hardRefresh: true })
            : await getProactivelyRefreshedToken();
          forceHardRefreshOnNextConnect = false;
          return {
            headers: {
              Authorization: `Bearer ${jwt}`,
              'x-hasura-role': desiredRole ?? role,
            },
          };
        },
        on: {
          closed: (event) => {
            const code = (event as CloseEvent | undefined)?.code;
            const reason = (event as CloseEvent | undefined)?.reason ?? '';
            if (JWT_SIGNATURE_FAILURE_PATTERN.test(reason)) {
              forceHardRefreshOnNextConnect = true;
              logger.warn(
                'ws closed due to JWT signature failure; will hard-refresh on next reconnect',
                { code, reason },
              );
            }
            // graphql-ws short-circuits retries on this set of close codes
            // regardless of shouldRetry/isFatalConnectionProblem — when one
            // arrives the active subscription will be killed and routed to
            // its error sink. Log at error level so this is visible in
            // alerts/dashboards. (onNonLazyError can't be used here: the
            // client runs in the default lazy mode, so that callback never
            // fires — see client.js:351-371 in graphql-ws@5.14.2.)
            const FATAL_CLOSE_CODES = new Set([
              4400, 4401, 4004, 4005, 4406, 4409, 4429, 4500,
            ]);
            if (code !== undefined && FATAL_CLOSE_CODES.has(code)) {
              logger.error('ws subscription closed with fatal code', { code });
            } else {
              logger.info('ws subscription closed', { code });
            }
          },
          error: (err) => {
            try {
              // Error instances drop their non-enumerable `message` through
              // JSON.stringify, so unwrap them explicitly.
              const serialized =
                typeof err === 'string'
                  ? err
                  : err instanceof Error
                    ? err.message
                    : JSON.stringify(err);
              if (JWT_SIGNATURE_FAILURE_PATTERN.test(serialized)) {
                forceHardRefreshOnNextConnect = true;
                logger.warn(
                  'ws errored due to JWT signature failure; will hard-refresh on next reconnect',
                );
              }
            } catch {
              // Some error shapes (e.g. circular refs) won't serialize —
              // fall through to the generic warn below.
            }
            logger.warn('ws subscription error', { err });
          },
        },
      }),
    );

    finalLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        );
      },
      wsLink,
      httpLink,
    );
  }

  return new ApolloClient({
    cache: cacheInstance,
    link: finalLink,
    defaultOptions,
    devtools: { enabled: process.env.NODE_ENV === 'development' },
  });
};
